import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { propertyRoleService } from '@iso/services';

/**
 * Safely read the current user ID from localStorage.
 * Returns null if localStorage is unavailable or contains corrupt data.
 */
const getCurrentUserId = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (!userJson) return null;
    const user = JSON.parse(userJson);
    return user?.id ?? null;
  } catch (err) {
    console.error('Failed to parse user from localStorage:', err);
    return null;
  }
};

/**
 * Module-level cache and in-flight promise tracker keyed by propertyId.
 * Multiple components calling this hook concurrently for the same property
 * will share a single network request rather than fanning out N requests.
 *
 * Cache lifetime: until propertyId changes or page reload.
 */
const rolesCache = new Map();      // propertyId -> roles response
const rolesInFlight = new Map();   // propertyId -> Promise

const fetchRolesForProperty = (propertyId) => {
  if (rolesCache.has(propertyId)) {
    return Promise.resolve(rolesCache.get(propertyId));
  }
  if (rolesInFlight.has(propertyId)) {
    return rolesInFlight.get(propertyId);
  }
  const promise = propertyRoleService.getAllRoles(propertyId)
    .then(res => {
      rolesCache.set(propertyId, res);
      rolesInFlight.delete(propertyId);
      return res;
    })
    .catch(err => {
      rolesInFlight.delete(propertyId);
      throw err;
    });
  rolesInFlight.set(propertyId, promise);
  return promise;
};

/**
 * Invalidate the roles cache for a property. Call this after role assignments change.
 */
export const invalidateSprayPermissionsCache = (propertyId) => {
  if (propertyId != null) {
    rolesCache.delete(propertyId);
  } else {
    rolesCache.clear();
  }
};

/**
 * Hook to check spray module permissions.
 * Returns whether the current user has privileged access (Agronomist, Admin, or Owner).
 *
 * @returns {{ isPrivileged: boolean, isAgronomist: boolean, isOwnerOrAdmin: boolean, loading: boolean }}
 */
const useSprayPermissions = () => {
  const activePermission = useSelector(state => state.permission?.activePermission);
  const propertyId = useSelector(state => state.property?.activePropertyId);
  const [isAgronomist, setIsAgronomist] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!propertyId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchRolesForProperty(propertyId)
      .then(res => {
        if (cancelled) return;
        if (res && res.roles) {
          const agronomistRole = res.roles.find(r => r.role_type === 'agronomist');
          if (agronomistRole && agronomistRole.users) {
            const userId = getCurrentUserId();
            if (userId != null) {
              // Coerce both sides to string — some endpoints return numeric IDs as strings
              const userIdStr = String(userId);
              const hasRole = agronomistRole.users.some(u => String(u.id) === userIdStr);
              setIsAgronomist(hasRole);
            }
          } else {
            setIsAgronomist(false);
          }
        }
      })
      .catch(err => {
        if (!cancelled) console.error('Error checking spray permissions:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const isOwnerOrAdmin = ['owner', 'admin'].includes(activePermission);
  const isPrivileged = isOwnerOrAdmin || isAgronomist;

  return { isPrivileged, isAgronomist, isOwnerOrAdmin, loading };
};

export default useSprayPermissions;
